import {type AxiosResponse} from 'axios';
import {type AppAxiosError, type SWRKeyEnum} from 'interfaces';
import Alert, {errorMessageHandler} from 'helpers/Alert';
import useSWR, {type Fetcher, type Key, mutate, type SWRConfiguration, type SWRResponse} from 'swr';
import {type OptionsObject, type SnackbarKey, type SnackbarMessage, useSnackbar} from 'notistack';

// eslint-disable-next-line @typescript-eslint/consistent-type-definitions
export declare type Stringable = {toString: () => string};
export declare type SWRresourceId = string | string[] | Stringable[];

const genericErrorHandler = (
  error,
  enqueueSnackbar: (message: SnackbarMessage, options?: OptionsObject) => SnackbarKey,
) => {
  const errorMessage = errorMessageHandler(error as AppAxiosError);
  enqueueSnackbar(errorMessage, {
    variant: 'error',
  });
};

function swrHookFactory<T>(
  key: SWRKeyEnum,
  fetcher: (...params: any[]) => Promise<AxiosResponse<T>>,
  options?: SWRConfiguration<T | undefined, undefined>,
) {
  return <W extends SWRResponse<T | undefined, any>>({
    resourceId,
    localOptions,
    errorHandler = genericErrorHandler,
  }: {
    resourceId?: SWRresourceId;
    localOptions?: SWRConfiguration<T | undefined, undefined>;
    errorHandler?: (
      error: any,
      enqueueSnackbar: (message: SnackbarMessage, options?: OptionsObject) => SnackbarKey,
    ) => void;
  }) => {
    const {enqueueSnackbar} = useSnackbar();

    const fetchResource: Fetcher<T | undefined> = async () => {
      try {
        const fetchParams = Array.isArray(resourceId) ? resourceId : [resourceId];
        const response = await fetcher(...fetchParams);
        const data = response.data;

        return data;
      } catch (error) {
        errorHandler(error, enqueueSnackbar);
      }
    };

    return useSWR<T | undefined, any>(resourceId ? [...buildSWRKey(resourceId), key] : null, fetchResource, {
      ...options,
      ...localOptions,
    }) as W;
  };
}

export function buildSWRKey(resourceId: SWRresourceId): string[] {
  if (typeof resourceId === 'string') {
    return [resourceId];
  }
  return resourceId.map((key: string | Stringable) => (typeof key === 'string' ? key : key.toString()));
}

/**
 * @deprecated SWR has native isLoading
 */
export const useSWRWithLoading = <T>(response: SWRResponse<T, any>) => {
  return {
    ...response,
    isLoading: !response.data && !response.error,
  };
};

export const mutateList = async <T extends {id: string}>({
  key,
  updateFunction,
  newEntry,
  revalidate = false,
}: {
  key: Key;
  updateFunction: Promise<AxiosResponse<T>>;
  newEntry?: T & Record<string, unknown>;
  revalidate?: boolean;
}) => {
  try {
    return await mutate<T[], AxiosResponse<T>>(key, updateFunction, {
      optimisticData: (current) => {
        if (current && newEntry) {
          return [...current, newEntry];
        }
        return current ?? [];
      },
      populateCache: (response, currentData) => {
        const updatedEntity = response.data;
        const method = response.config.method;
        const filteredData = (currentData ?? []).filter((currentEntity) => currentEntity.id !== updatedEntity.id);
        if (method?.toUpperCase() === 'DELETE') {
          return filteredData;
        }
        const index = currentData?.findIndex((currentEntity) => currentEntity.id === updatedEntity.id);
        if (index !== undefined && index >= 0) {
          filteredData.splice(index, 0, updatedEntity);
          return filteredData;
        }
        return [...filteredData, updatedEntity];
      },
      revalidate,
      rollbackOnError: true,
    });
  } catch (error) {
    Alert.error(error);
  }
};

export const mutateAll = async (swrKey: SWRKeyEnum) => {
  return await mutate((key) => {
    return Array.isArray(key) && key.length > 0 && key[key.length - 1] === swrKey;
  });
};

export const mutateAllByValue = async (value: string | Stringable) => {
  return await mutate((key) => {
    return Array.isArray(key) && key.length > 0 && key.includes(value);
  });
};

export default swrHookFactory;
